// Users › Drivers › a driver (wave 3, W2_Portal_BookingDetails look):
// "← Back" to the list (the page the person was on is handed back), the
// driver's strip (photo, name, phone, approval, online) with Refresh, then
// the tabs Info / Trips / Cars / Wallet / Donkey Miles / Bank details /
// Device.
//
// Data, exactly as before (no new reads): the entry from the loaded list,
// else ONE lookup of this driver (refreshUser withCars) once the list has
// settled; the one-user listener (subscribeToUser) while the page is open,
// detached on leave. Refresh reads the one record and its cars again.
import React, { useState, useEffect, useRef } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import DriverCars from './DriverCars';
import { BankDetails } from './BannkDetails';
import UserRides from './UserRides';
import UserWalletDetails from './UserWalletDetails';
import UserMilesDetails from './UserMilesDetails';
import UserInfoDetails, { UserSummaryCard } from './UserInfoDetails';
import DriverDeviceDetails from './DriverDeviceDetails';
import CircularLoading from '../components/CircularLoading';
import { EmptyState, InlineMessage, showToast } from '../components/feedback';
import { BackButton, calmCaps, usePageState, goBackOr } from '../components/modern';
import { tokens as c } from '../theme';

export default function DriverDetails() {
  const { id } = useParams();
  const { state } = useLocation();
  // The open tab is kept per person while staff open a trip and come back.
  const [value, setValue] = usePageState(`driver_details_${id}`, 'tab', 0);
  const { t, i18n } = useTranslation();
  const [data, setData] = useState([]);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const usersError = useSelector((state) => state.usersdata.error);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { subscribeToUser, refreshUser } = api;
  const [refreshing, setRefreshing] = useState(false);
  // A driver missing from the loaded list (a cache from before they signed
  // up, or a list that failed to load) is looked up once by id instead of
  // sending the agent to the 404 page: 'idle' | 'checking' | 'found' |
  // 'missing' (no such driver) | 'failed' (could not ask).
  const [lookup, setLookup] = useState('idle');
  const lookedUpRef = useRef(null);
  const currentIdRef = useRef(id);
  currentIdRef.current = id;

  // One user record (plus this driver's cars, indexed) — the same single
  // read the Refresh button makes; never the whole /users or /cars tree.
  const lookUpDriver = async () => {
    const askedFor = id;
    setLookup('checking');
    try {
      const rec = await dispatch(refreshUser(askedFor, { withCars: true }));
      if (currentIdRef.current !== askedFor) return;
      if (rec && rec.usertype === 'driver') {
        setData(rec);
        setLookup('found');
      } else {
        setLookup('missing');
      }
    } catch (_) {
      if (currentIdRef.current === askedFor) setLookup('failed');
    }
  };

  // Pull THIS driver's record + cars fresh on demand (one user, indexed cars) —
  // so an agent sees a mobile car/document upload immediately, without a full
  // /users or /cars re-download.
  const handleRefresh = async () => {
    if (!id || refreshing) return;
    setRefreshing(true);
    try {
      const rec = await dispatch(refreshUser(id, { withCars: true }));
      if (rec && rec.usertype === 'driver') {
        setData(rec);
        setLookup('found');
        showToast({ kind: 'success', message: t('driver_refreshed', { defaultValue: 'Driver details updated' }) });
      } else {
        setLookup('missing');
      }
    } catch (_) {
      showToast({ kind: 'error', message: t('refresh_failed', { defaultValue: 'Could not refresh. Check the internet connection, then try again.' }) });
    }
    setRefreshing(false);
  };

  useEffect(() => {
    const list = Array.isArray(staticusers) ? staticusers : null;
    const user = list ? list.filter((u) => u.id === id.toString() && u.usertype === 'driver')[0] : null;
    if (user) {
      setData(user);
      setLookup('found');
      return;
    }
    // Not (yet) in the list: wait while the list loads, then ask once.
    const listSettled = !!list || (!usersLoading && !!(usersError && usersError.flag));
    if (!listSettled) {
      setData([]);
      return;
    }
    if (lookedUpRef.current === id) return;
    lookedUpRef.current = id;
    setData([]);
    lookUpDriver();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticusers, id, usersLoading, usersError]);

  // Live subscription to this driver's /users record. Without this, the
  // wallet/topup/rating fields shown on the Wallet tab go stale the
  // moment the driver completes a trip on mobile (Cloud Function settles
  // server-side, but the admin's Redux only refreshes on full /users
  // refetch — could be hours away). Each settlement streams in as ~1-5 KB
  // over the existing wss connection. Auto-detaches on page unmount.
  useEffect(() => {
    if (!id) return undefined;
    const unsubscribe = dispatch(subscribeToUser(id));
    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch (_) {
        /* already detached */
      }
    };
  }, [id, dispatch, subscribeToUser]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Back to whatever opened this page (the list, a booking...), else the list.
  const goBack = () => goBackOr(navigate, '/users/1', { state: { pageNo: state?.pageNo } });

  const refreshButton = (
    <Button
      variant="outlined"
      color="neutral"
      onClick={handleRefresh}
      disabled={refreshing || lookup === 'checking'}
      startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshRoundedIcon />}
    >
      {refreshing ? t('refreshing', { defaultValue: 'Refreshing…' }) : t('refresh', { defaultValue: 'Refresh' })}
    </Button>
  );

  if (lookup === 'checking' || lookup === 'missing' || lookup === 'failed') {
    return (
      <Box dir={i18n.dir()} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <BackButton onClick={goBack} />
          {refreshButton}
        </Box>
        {lookup === 'checking' ? (
          <CircularLoading inline label={t('driver_looking_up', { defaultValue: 'Looking for this driver…' })} />
        ) : lookup === 'failed' ? (
          <InlineMessage
            kind="error"
            title={t('driver_load_failed', { defaultValue: 'Could not load this driver.' })}
            actionLabel={t('retry', { defaultValue: 'Retry' })}
            onAction={lookUpDriver}
          >
            {t('load_failed_body', { defaultValue: 'Check the internet connection, then try again.' })}
          </InlineMessage>
        ) : (
          <Paper>
            <EmptyState
              icon={<PersonSearchOutlinedIcon />}
              title={t('driver_not_found', { defaultValue: 'Driver not found in the loaded list' })}
              body={t('driver_not_found_body', {
                defaultValue: 'There is no driver with this ID. The account may have been deleted, or the link may be wrong. Refresh to check again.',
              })}
              actionLabel={t('refresh', { defaultValue: 'Refresh' })}
              onAction={lookUpDriver}
            />
          </Paper>
        )}
      </Box>
    );
  }

  // The tabs render while the list is still loading (as before): the Info
  // tab shows grey placeholders until the driver arrives. Inactive tabs
  // unmount, so each reads only when it is open.
  const tabs = [
    { key: 'info', label: t('info'), render: () => <UserInfoDetails data={data} /> },
    { key: 'trips', label: t('rides'), render: () => <UserRides data={data} tabid={1} /> },
    { key: 'cars', label: t('cars'), render: () => <DriverCars data={data} /> },
    { key: 'wallet', label: t('wallet'), render: () => <UserWalletDetails data={data} id={1} /> },
    { key: 'miles', label: t('donkey_miles'), render: () => <UserMilesDetails data={data} id={1} /> },
    { key: 'bank', label: calmCaps(t('bankDetails')), render: () => <BankDetails data={data} /> },
    { key: 'device', label: t('device_tab', { defaultValue: 'Device' }), render: () => <DriverDeviceDetails data={data} /> },
  ];
  const current = tabs[value] || tabs[0];

  return (
    <Box dir={i18n.dir()} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={goBack} />
      <UserSummaryCard user={data} action={refreshButton} />
      <Box sx={{ minWidth: 0 }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label={t('driver_details', { defaultValue: 'Driver details' })}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ borderBottom: `1px solid ${c.OUTLINE}` }}
        >
          {tabs.map((tab, i) => (
            <Tab key={tab.key} label={tab.label} id={`simple-tab-${i}`} aria-controls={`simple-tabpanel-${i}`} />
          ))}
        </Tabs>
        <Box role="tabpanel" id={`simple-tabpanel-${value}`} aria-labelledby={`simple-tab-${value}`} sx={{ pt: 2.5 }}>
          {current.render()}
        </Box>
      </Box>
    </Box>
  );
}
